import React, { useState, useEffect, useCallback } from "react";
import {
  Code,
  CheckCircle,
  XCircle,
  Loader,
  Send,
  Eye,
  RefreshCw,
  Plus,
  Trash2,
  AlertCircle,
  Copy,
  Star,
  MapPin,
  HelpCircle,
  Wrench,
} from "lucide-react";
import api from "../api/client";

// ─── Schema Preview ───
function SchemaPreview({ schema, title }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(schema, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-dark-100 rounded-xl border border-dark-50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-dark-50">
        <span className="text-xs font-medium text-gray-400">{title || "JSON-LD Schema"}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-copper transition-colors"
        >
          {copied ? <CheckCircle size={12} className="text-green-400" /> : <Copy size={12} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="p-4 text-xs text-gray-300 overflow-x-auto max-h-80">
        {JSON.stringify(schema, null, 2)}
      </pre>
    </div>
  );
}

// ─── LocalBusiness Schema Card ───
function LocalBusinessCard({ siteId, website }) {
  const [schema, setSchema] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [result, setResult] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editJson, setEditJson] = useState("");
  const [serviceTypes, setServiceTypes] = useState("");
  const [ratingValue, setRatingValue] = useState("");
  const [reviewCount, setReviewCount] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/websites/${siteId}/schema/status/`);
      setStatus(res.data);
      // Use custom schema if user previously saved one, otherwise auto-generated
      const activeSchema = res.data.custom_schema || res.data.generated_schema;
      setSchema(activeSchema);
      setEditJson(JSON.stringify(activeSchema, null, 2));
      if (res.data.is_custom) setEditing(true);
    } catch {
      // handled
    }
    setLoading(false);
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const handlePush = async () => {
    setPushing(true);
    setResult(null);
    try {
      let schemaToSend;
      if (editing) {
        try {
          schemaToSend = JSON.parse(editJson);
        } catch {
          setResult({ error: "Invalid JSON" });
          setPushing(false);
          return;
        }
      }

      const payload = {};
      if (schemaToSend) payload.schema = schemaToSend;
      if (serviceTypes.trim()) payload.service_types = serviceTypes.split(",").map(s => s.trim());
      if (ratingValue && reviewCount) {
        payload.aggregate_rating = { ratingValue, reviewCount };
      }

      const res = await api.post(`/websites/${siteId}/schema/push-sitewide/`, payload);
      setResult(res.data);
      load();
    } catch (err) {
      setResult({ error: err.response?.data?.error || "Push failed" });
    }
    setPushing(false);
  };

  const handlePreview = async () => {
    try {
      const payload = { dry_run: true };
      if (serviceTypes.trim()) payload.service_types = serviceTypes.split(",").map(s => s.trim());
      if (ratingValue && reviewCount) {
        payload.aggregate_rating = { ratingValue, reviewCount };
      }
      const res = await api.post(`/websites/${siteId}/schema/generate-push/`, payload);
      setSchema(res.data.schema);
      setEditJson(JSON.stringify(res.data.schema, null, 2));
      setEditing(false);
      // Update status to reflect auto-generated (not custom)
      setStatus(prev => prev ? { ...prev, is_custom: false, custom_schema: null } : prev);
    } catch {
      // handled
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size={24} className="animate-spin text-copper" />
      </div>
    );
  }

  return (
    <div className="bg-dark-200 rounded-xl border border-dark-50 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-copper/10 flex items-center justify-center">
            <MapPin size={20} className="text-copper" />
          </div>
          <div>
            <h3 className="text-white font-semibold">LocalBusiness Schema</h3>
            <p className="text-xs text-gray-500">Site-wide JSON-LD for local SEO ranking</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status?.is_custom && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-900/20 text-blue-400 flex items-center gap-1">
              <Code size={10} /> Custom
            </span>
          )}
          {status?.schema_pushed ? (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-900/20 text-green-400 flex items-center gap-1">
              <CheckCircle size={10} /> Pushed
            </span>
          ) : (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-900/20 text-amber-400 flex items-center gap-1">
              <AlertCircle size={10} /> Not pushed
            </span>
          )}
        </div>
      </div>

      {/* Configuration */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Service Types (comma-separated)</label>
          <input
            type="text"
            value={serviceTypes}
            onChange={(e) => setServiceTypes(e.target.value)}
            placeholder="e.g. Home Remodeling, Kitchen Renovation"
            className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Rating</label>
            <input
              type="text"
              value={ratingValue}
              onChange={(e) => setRatingValue(e.target.value)}
              placeholder="5.0"
              className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Reviews</label>
            <input
              type="text"
              value={reviewCount}
              onChange={(e) => setReviewCount(e.target.value)}
              placeholder="5"
              className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper"
            />
          </div>
        </div>
      </div>

      {/* Schema Preview */}
      {schema && !editing && <SchemaPreview schema={schema} title={status?.is_custom ? "Custom Schema (user-edited)" : "Generated LocalBusiness Schema"} />}

      {/* JSON Editor */}
      {editing && (
        <div className="mb-4">
          <textarea
            value={editJson}
            onChange={(e) => setEditJson(e.target.value)}
            rows={12}
            className="w-full bg-dark-100 border border-dark-50 rounded-lg px-4 py-3 text-xs font-mono text-gray-300 placeholder-gray-600 focus:outline-none focus:border-copper resize-none"
          />
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`mt-3 text-sm px-4 py-2.5 rounded-lg border ${
          result.error
            ? "bg-red-900/20 border-red-800/30 text-red-400"
            : "bg-green-900/20 border-green-800/30 text-green-400"
        }`}>
          {result.error || `Schema pushed successfully via ${result.method}`}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex gap-2">
          <button
            onClick={() => setEditing(!editing)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-dark-100 text-gray-400 border border-dark-50 hover:text-white transition-colors"
          >
            <Code size={14} />
            {editing ? "Preview" : "Edit JSON"}
          </button>
          <button
            onClick={handlePreview}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-dark-100 text-gray-400 border border-dark-50 hover:text-white transition-colors"
          >
            <RefreshCw size={14} />
            Regenerate
          </button>
        </div>
        <button
          onClick={handlePush}
          disabled={pushing}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-copper text-dark hover:bg-copper-600 transition-colors disabled:opacity-40"
        >
          {pushing ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
          {pushing ? "Pushing..." : "Push to WordPress"}
        </button>
      </div>
    </div>
  );
}

// ─── Service Schema Card ───
function ServiceSchemaCard({ siteId }) {
  const [services, setServices] = useState([{ name: "", description: "", url: "" }]);
  const [pushing, setPushing] = useState(null);
  const [results, setResults] = useState({});
  const [pushedServices, setPushedServices] = useState([]);

  // Load previously pushed service schemas
  useEffect(() => {
    if (!siteId) return;
    api.get(`/websites/${siteId}/schema/status/`).then(res => {
      const pushed = res.data.pushed_services || [];
      setPushedServices(pushed);
    }).catch(() => {});
  }, [siteId]);

  const addService = () => {
    setServices([...services, { name: "", description: "", url: "" }]);
  };

  const updateService = (i, field, value) => {
    const updated = [...services];
    updated[i][field] = value;
    setServices(updated);
  };

  const removeService = (i) => {
    setServices(services.filter((_, idx) => idx !== i));
  };

  const pushService = async (i) => {
    const svc = services[i];
    if (!svc.name || !svc.url) return;
    setPushing(i);
    try {
      const res = await api.post(`/websites/${siteId}/schema/service/`, {
        service_name: svc.name,
        service_description: svc.description,
        page_url: svc.url,
      });
      setResults({ ...results, [i]: res.data });
      // Reload pushed services list
      api.get(`/websites/${siteId}/schema/status/`).then(r => {
        setPushedServices(r.data.pushed_services || []);
      }).catch(() => {});
    } catch (err) {
      setResults({ ...results, [i]: { error: err.response?.data?.error || "Failed" } });
    }
    setPushing(null);
  };

  return (
    <div className="bg-dark-200 rounded-xl border border-dark-50 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-900/20 flex items-center justify-center">
            <Wrench size={20} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Service Schema</h3>
            <p className="text-xs text-gray-500">Add structured data to individual service pages</p>
          </div>
        </div>
        <button
          onClick={addService}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-dark-100 text-gray-400 border border-dark-50 hover:text-white transition-colors"
        >
          <Plus size={14} />
          Add Service
        </button>
      </div>

      {/* Previously pushed service schemas */}
      {pushedServices.length > 0 && (
        <div className="mb-4 space-y-2">
          <p className="text-xs text-gray-500 font-medium mb-2">Previously Pushed</p>
          {pushedServices.map((ps, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2 bg-dark-100 rounded-lg border border-dark-50 text-xs">
              <CheckCircle size={12} className="text-green-400 flex-shrink-0" />
              <span className="text-white font-medium">{ps.name || "Service"}</span>
              {ps.url && <span className="text-gray-500 truncate">{ps.url}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3">
        {services.map((svc, i) => (
          <div key={i} className="bg-dark-100 rounded-lg border border-dark-50 p-4">
            <div className="grid grid-cols-3 gap-3 mb-3">
              <input
                type="text"
                value={svc.name}
                onChange={(e) => updateService(i, "name", e.target.value)}
                placeholder="Service Name"
                className="bg-dark-200 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper"
              />
              <input
                type="text"
                value={svc.description}
                onChange={(e) => updateService(i, "description", e.target.value)}
                placeholder="Description (optional)"
                className="bg-dark-200 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper"
              />
              <input
                type="url"
                value={svc.url}
                onChange={(e) => updateService(i, "url", e.target.value)}
                placeholder="Page URL"
                className="bg-dark-200 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper"
              />
            </div>
            <div className="flex items-center justify-between">
              <button
                onClick={() => removeService(i)}
                className="text-xs text-gray-500 hover:text-red-400 transition-colors"
              >
                <Trash2 size={14} />
              </button>
              <div className="flex items-center gap-2">
                {results[i] && (
                  <span className={`text-[10px] ${results[i].error ? "text-red-400" : "text-green-400"}`}>
                    {results[i].error || "Pushed"}
                  </span>
                )}
                <button
                  onClick={() => pushService(i)}
                  disabled={pushing === i || !svc.name || !svc.url}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-copper/10 text-copper border border-copper/30 hover:bg-copper/20 transition-colors disabled:opacity-40"
                >
                  {pushing === i ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
                  Push
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── FAQ Schema Card ───
function FAQSchemaCard({ siteId }) {
  const [pageUrl, setPageUrl] = useState("");
  const [faqs, setFaqs] = useState([{ question: "", answer: "" }]);
  const [pushing, setPushing] = useState(false);
  const [result, setResult] = useState(null);

  const addFaq = () => {
    setFaqs([...faqs, { question: "", answer: "" }]);
  };

  const updateFaq = (i, field, value) => {
    const updated = [...faqs];
    updated[i][field] = value;
    setFaqs(updated);
  };

  const removeFaq = (i) => {
    setFaqs(faqs.filter((_, idx) => idx !== i));
  };

  const handlePush = async () => {
    const validFaqs = faqs.filter(f => f.question && f.answer);
    if (!pageUrl || validFaqs.length === 0) return;
    setPushing(true);
    setResult(null);
    try {
      const res = await api.post(`/websites/${siteId}/schema/faq/`, {
        page_url: pageUrl,
        questions: validFaqs,
      });
      setResult(res.data);
    } catch (err) {
      setResult({ error: err.response?.data?.error || "Failed" });
    }
    setPushing(false);
  };

  return (
    <div className="bg-dark-200 rounded-xl border border-dark-50 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-900/20 flex items-center justify-center">
            <HelpCircle size={20} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">FAQ Schema</h3>
            <p className="text-xs text-gray-500">Add FAQPage structured data to pages with FAQ sections</p>
          </div>
        </div>
        <button
          onClick={addFaq}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-dark-100 text-gray-400 border border-dark-50 hover:text-white transition-colors"
        >
          <Plus size={14} />
          Add Q&A
        </button>
      </div>

      <div className="mb-4">
        <label className="text-xs text-gray-500 mb-1 block">Page URL</label>
        <input
          type="url"
          value={pageUrl}
          onChange={(e) => setPageUrl(e.target.value)}
          placeholder="https://example.com/faq"
          className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper"
        />
      </div>

      <div className="space-y-3 mb-4">
        {faqs.map((faq, i) => (
          <div key={i} className="bg-dark-100 rounded-lg border border-dark-50 p-3">
            <div className="flex items-start gap-2">
              <div className="flex-1 space-y-2">
                <input
                  type="text"
                  value={faq.question}
                  onChange={(e) => updateFaq(i, "question", e.target.value)}
                  placeholder="Question"
                  className="w-full bg-dark-200 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper"
                />
                <textarea
                  value={faq.answer}
                  onChange={(e) => updateFaq(i, "answer", e.target.value)}
                  placeholder="Answer"
                  rows={2}
                  className="w-full bg-dark-200 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper resize-none"
                />
              </div>
              <button
                onClick={() => removeFaq(i)}
                className="text-gray-500 hover:text-red-400 transition-colors mt-2"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {result && (
        <div className={`mb-3 text-sm px-4 py-2.5 rounded-lg border ${
          result.error
            ? "bg-red-900/20 border-red-800/30 text-red-400"
            : "bg-green-900/20 border-green-800/30 text-green-400"
        }`}>
          {result.error || "FAQ schema pushed successfully"}
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handlePush}
          disabled={pushing || !pageUrl || faqs.every(f => !f.question || !f.answer)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-copper text-dark hover:bg-copper-600 transition-colors disabled:opacity-40"
        >
          {pushing ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
          {pushing ? "Pushing..." : "Push FAQ Schema"}
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───
export default function SchemaManager({ selectedSite }) {
  const siteId = selectedSite?.id;

  if (!selectedSite) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        Select a website to manage schema markup.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Schema Markup</h1>
        <p className="text-gray-500 text-sm mt-1">
          Generate and push structured data (JSON-LD) to improve local SEO rankings and rich results.
        </p>
      </div>

      <div className="space-y-6">
        <LocalBusinessCard siteId={siteId} website={selectedSite} />
        <ServiceSchemaCard siteId={siteId} />
        <FAQSchemaCard siteId={siteId} />
      </div>
    </div>
  );
}
